import React, { useState, useEffect } from "react";
import {
  TextField,
  MenuItem,
  Button,
  Box,
  Modal,
  Typography,
  Paper,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useAlert } from "../../components/Alert";
import instance from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import { RZP_KEY } from "../../globals/const";
import { useNavigate, useSearchParams } from "react-router-dom";
import LoaderContainer from "../../components/LoaderContainer";

const useStyles = makeStyles({
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    maxWidth: "400px",
    margin: "0 auto",
    padding: "16px",
  },
  submitButton: {
    marginTop: "16px",
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalPaper: {
    backgroundColor: "white",
    padding: "24px",
    borderRadius: "8px",
    maxWidth: "400px",
    width: "90%",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  },
  modalTitle: {
    marginBottom: "16px",
    fontWeight: "bold",
  },
  paymentDetail: {
    margin: "8px 0",
  },
  modalButtons: {
    display: "flex",
    gap: "12px",
    marginTop: "20px",
    justifyContent: "flex-end",
  },
});

const SubscriptionInvoicing = () => {
  const classes = useStyles();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    farm_id: searchParams.get("farm_id") || "",
    amount_per_acre: "",
    payment_mode: "UPI",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const openPaymentGateway = (orderData) => {
    const options = {
      key: RZP_KEY,
      currency: "INR",
      name: "Farmapp subscription",
      description: "Subscribe to farmapp",
      image:
        "https://farmapp-stage.asqi.in/static/media/FarmAppLogo.73816e8df129dc26c289.png",
      order_id: orderData.order_id,
      handler: function (response) {
        capturePayment(orderData, response);
      },
    };
    let rzp = new window.Razorpay(options);
    rzp.open();
  };

  const capturePayment = async (orderData, paymentData) => {
    setLoading(true);
    try {
      const payload = {
        order_key: orderData.order_key,
        payment_mode: orderData.payment_mode,
      };
      // For UPI, send Razorpay details
      if (orderData.payment_mode === "UPI") {
        payload.order_id = paymentData.razorpay_order_id;
        payload.payment_id = paymentData.razorpay_payment_id;
        payload.signature = paymentData.razorpay_signature;
      }
      // For CASH, send order_id from response
      if (orderData.payment_mode === "CASH") {
        payload.order_id = orderData.order_id;
      }
      const response = await instance.post(
        "/payments/capture-payment-create-invoice/",
        payload
      );
      console.log("Payment verified successfully:", response.data);
      setLoading(false);
      // Navigate user to /invoice/:invoiceId
      navigate(`/invoice/${response.data.id}`);
    } catch (error) {
      setLoading(false);
      console.error("Error verifying payment:", error);
      showAlert("error", "Payment verification failed");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await instance.post(
        "/payments/create-subscription-order/",
        formData
      );
      console.log("Form submitted successfully:", response.data);
      showAlert("success", "Payment details submitted successfully");
      if (response.data.payment_mode === "UPI") {
        openPaymentGateway(response.data);
      } else if (response.data.payment_mode === "CASH") {
        // Show modal with payment details for cash
        setPaymentDetails(response.data);
        setModalOpen(true);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleMarkPaid = async () => {
    setLoading(true);
    try {
      await capturePayment(paymentDetails, {});
      setModalOpen(false);
      setPaymentDetails(null);
    } catch (error) {
      setLoading(false);
      console.error("Error marking payment as paid:", error);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setPaymentDetails(null);
  };

  return (
    <LoaderContainer loading={loading}>
      <CustomAppBar />
      <Box
        component="form"
        className={classes.formContainer}
        onSubmit={handleSubmit}
      >
        <TextField
          label="Amount per Acre (Rs.)"
          name="amount_per_acre"
          type="number"
          value={formData.amount_per_acre}
          onChange={handleChange}
          required
          fullWidth
        />

        <TextField
          label="Payment Type"
          name="payment_mode"
          select
          value={formData.payment_mode}
          onChange={handleChange}
          fullWidth
        >
          <MenuItem value="CASH">Cash</MenuItem>
          <MenuItem value="UPI">UPI</MenuItem>
        </TextField>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          className={classes.submitButton}
        >
          Submit
        </Button>
      </Box>

      {/* Cash Payment Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        className={classes.modal}
      >
        <Paper className={classes.modalPaper}>
          <Typography variant="h6" className={classes.modalTitle}>
            Cash Payment Details
          </Typography>
          {paymentDetails && (
            <>
              <Typography className={classes.paymentDetail}>
                <strong>Farm Area:</strong> {paymentDetails.farm_area} acres
              </Typography>
              <Typography className={classes.paymentDetail}>
                <strong>Base Amount:</strong> ₹{paymentDetails.base_amount}
              </Typography>
              <Typography className={classes.paymentDetail}>
                <strong>GST Rate:</strong> {paymentDetails.gst_rate_percent}%
              </Typography>
              <Typography className={classes.paymentDetail}>
                <strong>GST Amount:</strong> ₹{paymentDetails.gst_amount}
              </Typography>
              <Typography className={classes.paymentDetail}>
                <strong>Total Amount:</strong> ₹{paymentDetails.total_amount}
              </Typography>
              <Typography className={classes.paymentDetail}>
                <strong>Payment Mode:</strong> {paymentDetails.payment_mode}
              </Typography>
            </>
          )}
          <Box className={classes.modalButtons}>
            <Button variant="outlined" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleMarkPaid}
            >
              Mark Paid
            </Button>
          </Box>
        </Paper>
      </Modal>
    </LoaderContainer>
  );
};

export default SubscriptionInvoicing;
